import { TrashSimple } from '@phosphor-icons/react'
import { Button } from 'antd'
import heic2any from 'heic2any'
import get from 'lodash/get'
import isNil from 'lodash/isNil'
import split from 'lodash/split'
import PropTypes from 'prop-types'
import * as React from 'react'
import ErrorLabel from 'ui/atoms/ErrorLabel'
import { sanitizeImage } from 'utils/attackPrevention/images'

import Tooltip from '@/ui/atoms/Tooltip'

import Ui from '../../../ui'
import Icons from '../../../ui/Icons'
import { downloadFromURL } from '../../../utils/downloadManager'
import { buildFullName, getImageUrl } from '../../../utils/helpers'
import ZentakeFormContext from '../ZentakeFormContext'

function FileUploadQuestion({ question, isOfficeUse }) {
  const formContext = React.useContext(ZentakeFormContext)
  const readonly = formContext.isQuestionReadonly(question)
  const answer = formContext.getAnswer(question)
  const isDirty = formContext.isQuestionDirty(question)
  const isValid = formContext.isQuestionValid(question)
  const [errorLabel, setErrorLabel] = React.useState('')

  const [isDismissed, setDismissed] = React.useState(false)
  const [hasSelectedFile, setHasSelectedFile] = React.useState(false)

  const fileRef = React.useRef()

  const [filePath, setFilePath] = React.useState(
    !!answer?.answer_files && answer.answer_files.length > 0
      ? answer.answer_files[0]
      : ''
  )

  const acceptedFileTypes = React.useMemo(() => {
    const fileExtensions = get(question, 'file_extensions', [])
    if (!fileExtensions.length) return undefined
    return fileExtensions.map((ext) => `.${ext}`).join(',')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.file_extensions])

  const initialPath = get(answer, 'answer_files', '')
  React.useEffect(() => {
    setFilePath(
      !!initialPath && initialPath.length > 0 ? getImageUrl(initialPath[0]) : ''
    )
  }, [initialPath])

  React.useEffect(() => {
    const hasFile =
      !!answer && !!answer.answer_files && answer.answer_files.length > 0

    setDismissed(!hasFile)
    setHasSelectedFile(hasFile)
  }, [answer])

  async function handleChange() {
    setErrorLabel('')
    if (get(fileRef, ['current', 'files', 'length'], 0)) {
      let file = await sanitizeImage(fileRef.current.files[0])

      const MAX_FILE_SIZE = 8 * 1024 * 1024

      if (file.size > MAX_FILE_SIZE) {
        fileRef.current.value = ''
        setErrorLabel('File size must be less than 8MB')
        return
      }

      const splittedFileName = file.name.split('.')
      let fileExtension = splittedFileName[splittedFileName.length - 1]

      if (fileExtension.toLowerCase() === 'heic') {
        file = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.5,
        })
        fileExtension = 'jpeg'
      }

      if (!question.file_extensions.includes(fileExtension)) {
        fileRef.current.value = ''
        setErrorLabel(
          `Invalid file type. Please upload a file with the following extensions: ${question.file_extensions.join(
            ', '
          )}`
        )
        return
      }

      const renamedFile = new File([file], file.name, {
        type: file.type,
      })

      formContext.onAnswerChanged(question, [renamedFile])
    } else {
      formContext.onAnswerChanged(question, null)
    }
  }

  function onDownloadPress() {
    const clientName =
      buildFullName(
        formContext?.client?.first_name,
        formContext?.client?.last_name
      ) || get(formContext, 'formName', '')
    const questionName = question?.text || split(filePath, '/').pop()
    const ext = split(filePath, '.').pop()
    const fileName = `${clientName} - ${questionName}.${ext}`
    if (filePath) {
      const completePath = getImageUrl(filePath)
      downloadFromURL(completePath, fileName)
    } else if (answer?.file) {
      downloadFromURL(answer.file, fileName)
    }
  }

  function onDeletePress() {
    formContext.onAnswerChanged(question, null)
    setDismissed(true)
    setHasSelectedFile(false)
  }

  const hasError = isDirty && question.is_required && !isValid

  return (
    <div>
      <div>
        {question.text && (
          <Ui.QuestionTitle
            isOfficeUse={isOfficeUse}
            isRequired={question.is_required}
          >
            {question.text}
          </Ui.QuestionTitle>
        )}
      </div>
      {readonly && !isNil(answer) ? (
        <div>
          <Ui.ButtonClear
            className="space-x-2 border"
            onClick={onDownloadPress}
          >
            <Icons.Download />
            <span>Download File</span>
          </Ui.ButtonClear>
        </div>
      ) : !isNil(answer) && !isDismissed && hasSelectedFile ? (
        <div className="space-x-2">
          <Ui.ButtonClear className="space-x-2 border" onClick={() => null}>
            <Icons.Check color="text-green-400" />
            <span>File Uploaded</span>
          </Ui.ButtonClear>
          <Tooltip mouseEnterDelay={0.35} title="Delete">
            <Button className="w-[fit-content] !px-4" onClick={onDeletePress}>
              <TrashSimple className="text-xl" />
            </Button>
          </Tooltip>
        </div>
      ) : (
        <>
          <Ui.FileInput
            accept={acceptedFileTypes}
            disabled={readonly}
            hasError={hasError}
            name={question.text}
            onChange={handleChange}
            ref={fileRef}
          />

          {(errorLabel || hasError) && (
            <ErrorLabel>{errorLabel || 'This field is required'}</ErrorLabel>
          )}
        </>
      )}
    </div>
  )
}

FileUploadQuestion.propTypes = {
  question: PropTypes.object.isRequired,
}

export default React.memo(FileUploadQuestion)
